import React, { useCallback, useMemo } from 'react';
import { Box, CardTypesEnum, OptionKey } from '@common-fe/common-fe';

import PlanTypeIcon from '@/modules/plan/PlanSetup/Sidebar/PlanTypeIcon';
import colors from '@/styles/colors';

import { PayoutOrderPlan } from '../../../PayoutDefinitionSetup.types';
import { usePlanTemplatesContext } from '../contexts/PlanTemplatesContext';

import PlanTemplatePickerDropdown from './PlanTemplatePickerDropdown/PlanTemplatePickerDropdown';
import { COLUMN_WIDTH_MAP, MAX_COLUMNS_COUNT } from './PayoutOrder.constants';

interface PayoutOrderPlanDropdownProps {
  testId: string;
  value: PayoutOrderPlan | null;
  onChange: (plan: PayoutOrderPlan | null) => void;
  disabled?: boolean;
  columnsCount: number;
  showWarningBanner?: boolean;
  placeholder?: string;
  errorMessage?: string;
}

const PayoutOrderPlanDropdown: React.FC<PayoutOrderPlanDropdownProps> = ({
  testId,
  value,
  onChange,
  disabled,
  columnsCount,
  showWarningBanner,
  placeholder,
  errorMessage,
}) => {
  const { formattedTemplates, isLoading } = usePlanTemplatesContext();

  const dropdownWidth = useMemo(
    () => COLUMN_WIDTH_MAP[columnsCount] || COLUMN_WIDTH_MAP[MAX_COLUMNS_COUNT],
    [columnsCount]
  );

  const handleChange = useCallback(
    (selectedKeys: OptionKey[]) => {
      const filtered = selectedKeys.filter(Boolean) as string[];
      if (!filtered.length) {
        onChange(null);
        return;
      }

      const key = filtered[0];

      const allPlans = formattedTemplates?.flatMap((category) => category.plans || []) || [];
      const selectedPlan = allPlans.find((p) => p.id === key);

      if (selectedPlan) {
        onChange({
          id: selectedPlan.id,
          name: selectedPlan.name,
          accountType: selectedPlan.type || '',
          order: 0,
        });
      }
    },
    [onChange, formattedTemplates]
  );

  const handleClear = useCallback(() => onChange(null), [onChange]);

  const planIcon = useMemo(() => {
    if (!value || !value.accountType) return undefined;
    const planType = value.accountType as CardTypesEnum;
    return (
      <PlanTypeIcon
        type={planType}
        size="20px"
        color={colors.cardTypes[planType as keyof typeof colors.cardTypes]}
      />
    );
  }, [value]);

  return (
    <Box data-testid={`${testId}_wrapper`}>
      <PlanTemplatePickerDropdown
        testId={testId}
        options={formattedTemplates || []}
        defaultValues={value ? [value.id] : []}
        onChange={handleChange}
        onClear={handleClear}
        isLoading={isLoading}
        isDisabled={disabled}
        width={dropdownWidth}
        columnsCount={columnsCount}
        showWarningBanner={showWarningBanner}
        placeholder={placeholder}
        leftIcon={value ? planIcon : undefined}
        showError={!!errorMessage}
        errorMessage={errorMessage}
      />
    </Box>
  );
};

export default React.memo(PayoutOrderPlanDropdown);
